import type { PropsWithChildren } from 'react';
import {
  createContext,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import type { Route } from 'universal-router';
import { useIsClient } from 'usehooks-ts';

import { getUserPermsSync } from '@/rbac';
import type { Permissions } from '@models';
import checkRoutePermission from '@routes/check-route-permission';
import { setPermissions } from '@store/actions/action-auth';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import type { AuthAccountState } from '@store/reducers/reducer-auth';
import { useRouteContext } from '@components/router';

export interface PermissionsContextType {
  /**
   * Whether the user is permitted to access the current route.
   */
  hasRoutePermission: boolean;
  /**
   * User permissions object scoped to the current route. Determines the actions
   * available to the user based on their role.
   */
  permissions: Permissions | null;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  permissions: null,
  hasRoutePermission: false,
});

export const useGetRoutePermissions = (account: AuthAccountState | null) =>
  useCallback(
    ({ actions }: Route) => {
      const value = getUserPermsSync({
        account,
        actions,
      });

      if (value instanceof Error) {
        console.error('Error getting permissions', value);
        return null;
      }

      return value;
    },
    [account],
  );

export const useGetNavPermissions = (account: AuthAccountState | null) =>
  useCallback(
    ({ navbar }: Route) => {
      const value = getUserPermsSync({
        account,
        actions: navbar?.permissions,
      });

      if (value instanceof Error) {
        console.error('Error getting permissions', value);
        return null;
      }

      return value;
    },
    [account],
  );

/**
 * Context provider carrying permissions to avoid prop-drilling from the route.
 *
 * Our current auth architecture has a dependency on the route context in order
 * to resolve scoped permissions. In turn, the route permission flag cannot be
 * determined until permissions are set, which needs to be handled consistently
 * whether on the server or client. The indeterminate state is painful to handle
 * deep in the tree, so this provider attempts to centralize it and pass down a
 * stable state.
 */
export const PermissionsProvider = ({ children }: PropsWithChildren) => {
  const isClient = useIsClient();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const account = useAppSelector(state => state.auth.account);

  // Need the eager state here to prioritize the permissions check
  const { route } = useRouteContext({ deferred: false });

  const getRoutePermissions = useGetRoutePermissions(account);

  const context = useMemo(() => {
    const permissions = getRoutePermissions(route);

    const hasRoutePermission = isAuthenticated
      ? checkRoutePermission({ route, permissions })
      : false;

    return {
      permissions,
      hasRoutePermission,
    };
  }, [getRoutePermissions, route, isAuthenticated]);

  useEffect(() => {
    if (!isClient) return;

    startTransition(() => {
      // Resolved permissions have to be synced to the auth store
      // TODO: This feels like a smell, we should clean this up sometime
      dispatch(setPermissions(context.permissions));
    });
  }, [isClient, context.permissions]);

  return (
    <PermissionsContext.Provider value={context}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsProvider;
